import React from 'react';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  TicketIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { formatDate } from '../../../utils/dateUtils';
import { Modal } from '../../common';
import { useConcessionTypes, poolDisplayName, resolvePoolBalance } from '../../../utils/concessions';

/**
 * Format duration in minutes to human-readable format
 */
const formatDuration = (duration) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Class booking modal component
 */
const ClassBookingModal = ({
  isOpen,
  onClose,
  classData,
  user,
  onBook,
  onCancel,
  onJoinWaitlist,
  onLeaveWaitlist,
  isWithin30Minutes,
  isWithin1Hour,
  isWithin24Hours,
  isAtConcessionLimit,
  canJoinWaitlist,
  isUserBooked,
  isUserOnWaitlist
}) => {
  const { types: typeNames } = useConcessionTypes(!!user);
  if (!classData) return null;

  const booked = isUserBooked(classData);
  const onWaitlist = isUserOnWaitlist(classData);
  const within30Min = isWithin30Minutes(classData);
  const within1Hour = isWithin1Hour(classData);
  const within24Hours = isWithin24Hours(classData);
  const atConcessionLimit = isAtConcessionLimit(classData);
  const canJoin = canJoinWaitlist(classData);
  // The pool this class draws from, and the user's balance in it
  const poolName = classData.concessionType ? poolDisplayName(typeNames, classData.concessionType) : null;
  const poolBalance = resolvePoolBalance(user, classData.concessionType);

  const renderFooterButtons = () => {
    if (booked) {
      return (
        <button
          onClick={() => onCancel(classData)}
          disabled={within30Min}
          className={`btn-danger ${within30Min ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {within30Min ? 'Cancellation Closed' : 'Cancel Booking'}
        </button>
      );
    }

    if (onWaitlist) {
      if (classData.isFull) {
        return (
          <div className="text-sm text-secondary text-center">
            You are on the waitlist
          </div>
        );
      }
      return (
        <button onClick={() => onBook(classData)} className="btn-primary">
          Book Now
        </button>
      );
    }

    if (classData.isFull) {
      if (within1Hour) {
        return (
          <div className="px-4 py-2 rounded-md text-sm font-medium text-muted bg-tertiary border border-primary">
            Waitlist closed
          </div>
        );
      }
      if (!canJoin) {
        return (
          <div className="px-4 py-2 rounded-md text-sm font-medium text-muted bg-tertiary border border-primary">
            Insufficient concessions
          </div>
        );
      }
      return (
        <button onClick={() => onJoinWaitlist(classData)} className="btn-warning">
          Join Waitlist
        </button>
      );
    }

    return (
      <button
        onClick={() => onBook(classData)}
        disabled={within30Min || atConcessionLimit}
        className={`btn-primary ${(within30Min || atConcessionLimit) ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {within30Min ? 'Booking closed' : atConcessionLimit ? 'Insufficient concessions' : 'Confirm Booking'}
      </button>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Book Class"
      footer={
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className="btn-secondary mr-4">
            Cancel
          </button>
          {renderFooterButtons()}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Class Information */}
        <div>
          <h4 className="text-lg font-semibold text-primary">{classData.name}</h4>
          <p className="text-sm text-secondary mt-1">{classData.description}</p>
        </div>

        {/* Instructor */}
        <div className="flex items-center text-sm text-secondary">
          <UserGroupIcon className="h-4 w-4 mr-2" />
          <span>{classData.instructor}</span>
        </div>

        {/* Time and Duration */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-secondary">
            <CalendarIcon className="h-4 w-4 mr-2" />
            <span>{formatDate(classData.startTime, 'MMM d, yyyy h:mm a')}</span>
          </div>
          <div className="flex items-center text-secondary">
            <ClockIcon className="h-4 w-4 mr-2" />
            <span>{formatDuration(classData.duration)}</span>
          </div>
        </div>

        {/* Concessions Required */}
        <div className="flex items-center text-sm text-secondary">
          <TicketIcon className="h-4 w-4 mr-2" />
          <span>
            Concessions required: <span className="font-medium text-primary">{classData.concessionsRequired || 1}</span>
            {poolName && user && (
              <> · Your {poolName} balance:{' '}
                <span className={`font-medium ${poolBalance < 0 ? 'text-red-500' : 'text-primary'}`}>
                  {poolBalance}
                </span>
              </>
            )}
          </span>
        </div>

        {/* Low Concessions Warning */}
        {user && poolBalance <= 2 && !booked && (
          <div className="low-concession-modal-warning">
            <ExclamationTriangleIcon className="low-concession-modal-warning-icon" />
            <span className="low-concession-modal-warning-text">
              You only have {poolBalance} concession{poolBalance === 1 ? '' : 's'} left{poolName ? ` in ${poolName}` : ''}
            </span>
          </div>
        )}

        {/* Location */}
        <div className="flex items-center text-sm text-secondary">
          <MapPinIcon className="h-4 w-4 mr-2" />
          <span>{classData.location} {classData.room}</span>
        </div>

        {/* Available Spots */}
        <div className="pt-2 border-t border-primary">
          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary">Available spots:</span>
            <span className="font-medium text-primary">
              {classData.availableSpots} / {classData.maxCapacity}
            </span>
          </div>
        </div>

        {/* Booking Restriction Notice */}
        {!booked && !onWaitlist && within30Min && (
          <div className="pt-2 border-t border-primary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary">Booking:</span>
              <span className="booking-restriction-badge-inline">
                Unable to book within 30 minutes
              </span>
            </div>
          </div>
        )}

        {/* Refunds Section */}
        {booked && within24Hours && (
          <div className="pt-2 border-t border-primary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary">Refunds:</span>
              <span className="refund-warning-badge-inline">
                No refund within 24hrs
              </span>
            </div>
          </div>
        )}

        {/* Concession Limit Notice */}
        {!booked && !onWaitlist && atConcessionLimit && (
          <div className="pt-2 border-t border-primary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary">Booking:</span>
              <span className="concession-limit-badge-inline">
                You must acquire more concessions to book
              </span>
            </div>
          </div>
        )}

        {/* Waitlisted Section */}
        {onWaitlist && (
          <div className="pt-2 border-t border-primary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-secondary">Waitlisted:</span>
              <button
                onClick={() => onLeaveWaitlist(classData)}
                className="leave-waitlist-link"
              >
                Leave Waitlist
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClassBookingModal;
